import React from 'react';
import { Circle, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { hk_lat_lng } from '@/constants/map';
import { getBoundingBoxCenter } from '@/utils/map';
import { LatLng } from '@/models/core.model';


export type MarkerInfo = {
  position: LatLng;
  label?: string;
  tooltip?: string;
  key: string;
  radius?: number;
};

type MapMultipleSelectorProps = {
  value: MarkerInfo[];
  onClick?: (position: LatLng) => void;
  onMarkerDrag?: (key: string, position: LatLng) => void;
  onMarkerClick?: (key: string, position: LatLng) => void;
  defaultCenter?: LatLng;
  defaultZoom?: number;
  height?: string;
  disabled?: boolean;
};

const MapMultipleSelector: React.FC<MapMultipleSelectorProps> = ({
  value,
  onClick,
  onMarkerDrag,
  onMarkerClick,
  defaultCenter = hk_lat_lng, // hongkong location
  defaultZoom = value.length > 0 ? 15 : 8,
  height = '350px',
  disabled = false,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });
  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if(disabled){
      return;
    }
    if (e.latLng) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      onClick && onClick(position);
    }
  };
  const handleMarkerDrag = (key: string, e: google.maps.MapMouseEvent) => {
    if(disabled){
      return;
    }
    if (e.latLng) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      onMarkerDrag && onMarkerDrag(key, position);
    }
  };
  const handleMarkerClick = (key: string, e: google.maps.MapMouseEvent) => {
    if(disabled){
      return;
    }
    if (e.latLng) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      onMarkerClick && onMarkerClick(key, position);
    }
  };
  
  if (!isLoaded) return <h6>Loading Map...</h6>;

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height, borderRadius: 10 }}
      center={getBoundingBoxCenter(value.map(v => v.position)) ?? defaultCenter}
      zoom={defaultZoom}
      onClick={handleMapClick}
    >
      {value.map(point => 
         <React.Fragment key={point.key}>
          <Marker
            title={point.tooltip}
            key={point.key} 
            position={point.position} 
            label={point.label} 
            onDragEnd={e => handleMarkerDrag(point.key, e)}
            onClick={e => handleMarkerClick(point.key, e)}
            draggable
          />
          {point.radius && <Circle
            center={point.position}
            radius={point.radius}
            options={{
              strokeColor: '#0088ff',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: '#0088ff',
              fillOpacity: 0.2
            }}
          />}
        </React.Fragment>
      )}

    </GoogleMap>
  );
};

export default MapMultipleSelector;
